import { createTheme } from '@mui/material/styles'

/** Institutional accent colours shared by both colour schemes. */
const DOLE_BLUE = '#0b4a8f'
const DOLE_GOLD = '#f2b705'

/**
 * Application theme.
 *
 * A restrained government palette: DOLE blue as the primary action colour,
 * gold as the accent, and a soft grey canvas so data tables read clearly.
 *
 * Uses MUI's colour-scheme API (CSS theme variables) so the UI supports both
 * `light` and `dark`. The default mode is `system`, which follows the user's
 * operating-system preference; users can pin a mode with `useColorScheme()`
 * from the account menu.
 *
 * `colorSchemeSelector: 'class'` is required for manual toggling — the default
 * `'media'` selector only reacts to the OS setting, so `setMode()` would be a
 * no-op. The active scheme is applied as the `light` / `dark` class on <html>.
 */
export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'class' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: DOLE_BLUE, dark: '#08386d', light: '#3f76b5', contrastText: '#ffffff' },
        secondary: { main: DOLE_GOLD, contrastText: '#3a2c00' },
        background: { default: '#f4f6fb', paper: '#ffffff' },
        text: { primary: '#16233a', secondary: '#5a6b7f' },
        divider: 'rgba(11, 74, 143, 0.12)',
        success: { main: '#1b7f4b' },
        warning: { main: '#b26a00' },
        info: { main: '#1565c0' },
        error: { main: '#c62828' },
      },
    },
    dark: {
      palette: {
        primary: { main: '#5b9bd5', dark: '#3f76b5', light: '#8ab6e6', contrastText: '#05121f' },
        secondary: { main: DOLE_GOLD, contrastText: '#3a2c00' },
        background: { default: '#0e1524', paper: '#16203a' },
        text: { primary: '#e8eefb', secondary: '#9fb0c9' },
        divider: 'rgba(138, 182, 230, 0.18)',
        success: { main: '#4caf7d' },
        warning: { main: '#e0a33a' },
        info: { main: '#64b5f6' },
        error: { main: '#ef5350' },
      },
    },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Google Sans", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: ({ theme }) => [
          {
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(11, 74, 143, 0.12)',
          },
          theme.applyStyles('dark', {
            backgroundColor: 'rgba(14, 21, 36, 0.88)',
            borderBottom: '1px solid rgba(138, 182, 230, 0.18)',
          }),
        ],
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: { defaultProps: { variant: 'outlined' } },
    MuiButton: { defaultProps: { disableElevation: true } },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => [
          {
            borderRadius: 8,
            '&.Mui-selected': { backgroundColor: 'rgba(11, 74, 143, 0.10)' },
          },
          theme.applyStyles('dark', {
            '&.Mui-selected': { backgroundColor: 'rgba(91, 155, 213, 0.18)' },
          }),
        ],
      },
    },
    MuiTableCell: { styleOverrides: { head: { fontWeight: 700, whiteSpace: 'nowrap' } } },
  },
})
